/**
 * API Service Client
 * Handles all communication with the Cloudflare Workers backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://realestatecost2.harsha-4cf.workers.dev';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Project {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  retail_type?: string;
  total_nra?: number;
  analysis_start_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CalculationResults {
  projectId: string;
  monthlyCashFlows: MonthlyCashFlow[];
  annualCashFlows: AnnualCashFlow[];
  returnMetrics: ReturnMetrics;
  reversion: Reversion;
  totalProjectCost: number;
  totalEquityInvested: number;
  totalDebtFinancing: number;
  stabilizedNOI: number;
  yieldOnCost: number;
  developmentSpread: number;
  noiCapAtStabilization: number;
  totalMonths: number;
  totalYears: number;
}

export interface MonthlyCashFlow {
  month: number;
  date: string;
  period: string;
  landCosts: number;
  hardCosts: number;
  softCosts: number;
  developmentCosts: number;
  revenue: MonthlyRevenue;
  expenses: MonthlyExpenses;
  noi: number;
  debtDraws: number;
  interestExpense: number;
  principalPayment: number;
  debtService: number;
  loanBalance: number;
  cashFlowBeforeFinancing: number;
  cashFlowAfterFinancing: number;
  equityCashFlow: number;
  cumulativeEquity: number;
  cumulativeDebt: number;
}

export interface MonthlyRevenue {
  month: number;
  baseRent: number;
  recoveries: number;
  otherIncome: number;
  totalRevenue: number;
  vacancy: number;
  effectiveRevenue: number;
}

export interface MonthlyExpenses {
  month: number;
  propertyTaxes: number;
  insurance: number;
  utilities: number;
  maintenance: number;
  management: number;
  marketing: number;
  other: number;
  totalExpenses: number;
}

export interface AnnualCashFlow {
  year: number;
  revenue: number;
  expenses: number;
  noi: number;
  debtService: number;
  cashFlow: number;
}

export interface ReturnMetrics {
  leveredIRR: number;
  unleveredIRR: number;
  equityMultiple: number;
  averageCashOnCash: number;
  totalCashInvested: number;
  totalCashReturned: number;
}

export interface Reversion {
  reversionMonth: number;
  stabilizedNOI: number;
  exitCapRate: number;
  grossSalePrice: number;
  sellingCosts: number;
  brokerCommission: number;
  totalTransactionCosts: number;
  netSaleProceeds: number;
  loanPayoff: number;
  netProceedsToEquity: number;
}

/**
 * API Client Class
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Projects
  async getProjects(): Promise<ApiResponse<Project[]>> {
    return this.request<Project[]>('/api/projects');
  }

  async getProject(id: string): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/api/projects/${id}`);
  }

  async createProject(project: Partial<Project>): Promise<ApiResponse<Project>> {
    return this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    });
  }

  async updateProject(
    id: string,
    project: Partial<Project>
  ): Promise<ApiResponse<Project>> {
    return this.request<Project>(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    });
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Calculations
  async runCalculations(inputs: any): Promise<ApiResponse<{ results: CalculationResults; savedAt: string }>> {
    return this.request<{ results: CalculationResults; savedAt: string }>('/api/calculations/run', {
      method: 'POST',
      body: JSON.stringify(inputs),
    });
  }

  async getCalculationResults(
    projectId: string
  ): Promise<ApiResponse<CalculationResults>> {
    return this.request<CalculationResults>(
      `/api/calculations/${projectId}/results`
    );
  }

  async getCalculationSummary(projectId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/api/calculations/${projectId}/summary`);
  }

  async deleteCalculations(projectId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/calculations/${projectId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<{ status: string }>> {
    return this.request<{ status: string }>('/health');
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export class for testing
export { ApiClient };
